'use client';

import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { Buildings } from 'phosphor-icons';
import { useLayoutEffect, useRef, useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { Avatar } from '../../atoms/Avatar/Avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../atoms/Tooltip/TooltipRadix';

import type {
  AccountTypeToggleGroupProps,
  AccountTypeToggleGroupValue,
} from './AccountTypeToggleGroup.types';

type TruncatedTextWithTooltipProps = {
  text: string;
  className?: string;
  isSelected?: boolean;
};

type AccountTypeItemProps = {
  value: AccountTypeToggleGroupValue;
  avatar: React.ReactNode;
  title: string;
  subtitle: string;
  isSelected: boolean;
  ariaLabel: string;
};

const TruncatedTextWithTooltip = ({
  text,
  className = '',
  isSelected = false,
}: TruncatedTextWithTooltipProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [text]);

  const reference = (
    <span
      className={c(
        'block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left text-xs font-normal leading-[18px] tracking-[-0.024px]',
        isSelected ? 'text-blue-200' : 'text-grey-400',
        className
      )}
      ref={ref}
    >
      [{text}]
    </span>
  );

  if (!isTruncated) {
    return reference;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{reference}</TooltipTrigger>
      <TooltipContent
        arrowClassName="fill-grey-700"
        className="z-[60] max-w-[250px] break-words rounded-sm bg-grey-700 px-4 py-3 text-xs text-white"
      >
        [{text}]
      </TooltipContent>
    </Tooltip>
  );
};

const AccountTypeItem = ({
  value,
  avatar,
  title,
  subtitle,
  isSelected,
  ariaLabel,
}: AccountTypeItemProps) => (
  <ToggleGroup.Item
    value={value}
    aria-label={ariaLabel}
    className={c(
      'relative flex w-1/2 flex-shrink-0 cursor-pointer items-center gap-3 pb-2 pl-3 pr-2 pt-2',
      'transition-colors focus-visible:outline-none focus-visible:ring-2',
      'focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900',
      isSelected ? 'bg-blue/20' : 'bg-grey-800 hover:bg-grey-800/50'
    )}
  >
    {avatar}
    <div className="flex min-w-0 flex-1 flex-col items-start">
      <span className="text-base font-normal leading-6 tracking-tighter text-grey-100">
        {title}
      </span>
      <TruncatedTextWithTooltip text={subtitle} isSelected={isSelected} />
    </div>
  </ToggleGroup.Item>
);

export const AccountTypeToggleGroup = ({
  userName,
  userProfilePicture,
  publisherName,
  value,
  microcopy,
  onValueChange,
  className = '',
}: AccountTypeToggleGroupProps) => {
  const handleChange = (newValue: string) => {
    if (newValue) {
      onValueChange(newValue as AccountTypeToggleGroupValue);
    }
  };

  const isPersonalSelected = value === 'personal';
  const isOrganizationSelected = value === 'organization';

  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={handleChange}
      aria-label={microcopy.ariaLabel}
      className={c('flex w-full overflow-hidden rounded-lg', className)}
    >
      <AccountTypeItem
        value="personal"
        avatar={
          <Avatar
            userName={userName}
            userProfilePicture={userProfilePicture}
            size={32}
            className={c(
              'avatar-icon hidden font-medium md:flex ',
              isPersonalSelected ? 'bg-blue' : 'bg-grey-900'
            )}
            textClassName="text-[13.333px] text-white"
          />
        }
        title={microcopy.personal}
        subtitle={userName}
        isSelected={isPersonalSelected}
        ariaLabel={`${microcopy.personal}: ${userName}`}
      />
      <AccountTypeItem
        value="organization"
        avatar={
          <div
            className={c(
              'avatar-icon hidden h-8 w-8 items-center justify-center rounded-full transition-colors md:flex',
              isOrganizationSelected ? 'bg-blue' : 'bg-grey-900'
            )}
          >
            <Buildings
              size={16}
              weight="bold"
              aria-hidden="true"
              focusable="false"
              className="text-grey-100"
            />
          </div>
        }
        title={microcopy.organization}
        subtitle={publisherName}
        isSelected={isOrganizationSelected}
        ariaLabel={`${microcopy.organization}: ${publisherName}`}
      />
    </ToggleGroup.Root>
  );
};
